import * as React from 'react'
import { AlertCircle, Loader2, Pencil, RotateCw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useCommitOnUnmount } from '@/hooks/use-unsaved'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'

/** Standard page frame: consistent max width, padding, and header layout. */
export function Page({
  title,
  description,
  actions,
  children,
  wide,
}: {
  /**
   * Usually a string. A node is how a page makes its own title editable without
   * every page growing the machinery for it — see `EditableTitle` below.
   */
  title: React.ReactNode
  description?: React.ReactNode
  actions?: React.ReactNode
  children: React.ReactNode
  wide?: boolean
}) {
  return (
    <div className={cn('mx-auto w-full px-4 py-6 sm:px-6 lg:py-8', wide ? 'max-w-[1600px]' : 'max-w-6xl')}>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  )
}

/**
 * A page title that can be renamed where it is read.
 *
 * Click the heading, type, Enter or blur to save; Escape puts back what was
 * there. In place rather than behind a dialog because a rename is one field and
 * almost always a correction — a typo in a name typed during a meeting — and a
 * modal with its own Save button is more ceremony than fixing a typo deserves.
 *
 * It renders no heading of its own: `Page` supplies the `<h1>` and this stays a
 * control inside it, so a caller cannot end up with two nested headings.
 *
 * `useCommitOnUnmount` is not optional here. No browser fires `blur` for a
 * focused element that has been removed from the DOM, so a route change or a
 * refetch mid-rename drops the edit silently — the same hole the note fields in
 * `evaluation-detail.tsx` and `board-canvas.tsx` document. `committed` is the
 * latch that stops blur and unmount writing the same name twice.
 *
 * An empty name is put back rather than saved: these columns are `not null`,
 * and a row named "" cannot be found again in the list it came from.
 */
export function EditableTitle({
  value,
  onRename,
  label = 'Rename',
  readOnly,
}: {
  value: string
  onRename: (name: string) => void
  /** The control's accessible name, e.g. "Rename evaluation". */
  label?: string
  readOnly?: boolean
}) {
  const [editing, setEditing] = React.useState(false)
  const [draft, setDraft] = React.useState(value)
  const committed = React.useRef(value)

  // The row moves under us for ordinary reasons — a refetch, another tab, a
  // refused write putting the old name back. Adopt it, unless this screen is
  // the one mid-edit.
  React.useEffect(() => {
    committed.current = value
    if (!editing) setDraft(value)
  }, [value, editing])

  const commit = () => {
    const next = draft.trim()
    if (!next || next === committed.current) {
      setDraft(committed.current)
      return
    }
    committed.current = next
    onRename(next)
  }
  useCommitOnUnmount(() => {
    if (editing) commit()
  })

  if (readOnly) return <>{value}</>

  if (editing) {
    return (
      <input
        autoFocus
        aria-label={label}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          commit()
          setEditing(false)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
            setEditing(false)
          } else if (e.key === 'Escape') {
            e.preventDefault()
            setDraft(committed.current)
            setEditing(false)
          }
        }}
        className="-mx-2 w-full max-w-2xl rounded-md border border-input bg-background px-2 py-0.5 text-2xl font-semibold tracking-tight focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      />
    )
  }

  return (
    <button
      type="button"
      title={label}
      onClick={() => {
        setDraft(value)
        setEditing(true)
      }}
      className="group -mx-2 flex max-w-full items-center gap-2 rounded-md px-2 py-0.5 text-left hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
    >
      <span className="truncate">{value}</span>
      <Pencil className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
    </button>
  )
}

/**
 * A date that reads as prose until someone clicks it, then a native picker.
 *
 * It commits on CHANGE rather than on blur, which is what a date input wants: a
 * half-typed date never reaches the handler, the browser only reports a value
 * once all three segments are filled, and the picker reports the moment a day
 * is chosen. That also means there is nothing uncommitted to rescue on unmount,
 * unlike `EditableTitle` above — so the field stays open after a save and a
 * second adjustment is just a second write.
 *
 * The value NEVER goes through `new Date()`. These are date-only columns,
 * `<input type="date">` speaks the same `YYYY-MM-DD`, and parsing in between is
 * precisely how a date starts rendering as the day before in every negative UTC
 * offset — the bug `localDate` in `lib/format.ts` exists for.
 */
export function EditableDate({
  value,
  display,
  onChange,
  label = 'Change date',
}: {
  /** `YYYY-MM-DD`, exactly as the column stores it. */
  value: string
  /** How it reads when nobody is editing it. */
  display: React.ReactNode
  onChange: (next: string) => void
  label?: string
}) {
  const [editing, setEditing] = React.useState(false)

  if (editing) {
    return (
      <input
        type="date"
        autoFocus
        aria-label={label}
        defaultValue={value}
        onChange={(e) => {
          if (e.target.value) onChange(e.target.value)
        }}
        onBlur={() => setEditing(false)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === 'Escape') {
            e.preventDefault()
            setEditing(false)
          }
        }}
        className="rounded-md border border-input bg-background px-1.5 py-0.5 text-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      />
    )
  }

  return (
    <button
      type="button"
      title={label}
      onClick={() => setEditing(true)}
      className="group -mx-1 inline-flex items-center gap-1 rounded px-1 hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
    >
      {display}
      <Pencil className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
    </button>
  )
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon: React.ComponentType<{ className?: string }>
  title: string
  description?: string
  action?: React.ReactNode
}) {
  return (
    <Card className="border-dashed shadow-none">
      <CardContent className="flex flex-col items-center gap-3 px-6 py-14 text-center">
        <div className="rounded-full bg-muted p-3">
          <Icon className="h-5 w-5 text-muted-foreground" />
        </div>
        <div>
          <p className="font-medium">{title}</p>
          {description && (
            <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{description}</p>
          )}
        </div>
        {action}
      </CardContent>
    </Card>
  )
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      {label}
    </div>
  )
}

/**
 * A failed read, named — and, where the caller can offer one, a way out of it.
 *
 * `onRetry` is optional because most callers have nothing useful to retry with,
 * but a page whose whole body is one query should always pass it: PostgREST
 * answers 503 when it cannot get a database connection and does not retry past
 * the two attempts in `lib/supabase.ts`, so the failure a person actually meets
 * is usually transient and the only recovery otherwise is reloading the tab.
 */
export function ErrorNote({ error, onRetry }: { error: string; onRetry?: () => void }) {
  return (
    <Card className="border-destructive/40 bg-destructive/5 shadow-none">
      <CardContent className="flex items-start gap-3 py-4">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
        <div className="min-w-0 text-sm">
          <p className="font-medium text-destructive">Something went wrong</p>
          <p className="mt-0.5 break-words text-muted-foreground">{error}</p>
          {onRetry && (
            <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
              <RotateCw /> Try again
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Very small Markdown subset for activity notes, agendas, and outcomes:
 * headings, bold/italic, inline code, links, bullet and numbered lists,
 * fenced code, and paragraphs.
 *
 * Hand-rolled rather than pulling in a Markdown library because the input is
 * written by team members inside the portal and rendered right back to them —
 * a full parser plus a sanitizer is a lot of surface area for four formatting
 * features. Everything is escaped first, so no raw HTML from the DB ever
 * reaches the DOM.
 */
export function Markdown({ source, className }: { source: string; className?: string }) {
  const html = React.useMemo(() => renderMarkdown(source), [source])
  return (
    <div
      className={cn(
        'space-y-3 text-sm leading-relaxed',
        '[&_h2]:mt-5 [&_h2]:text-base [&_h2]:font-semibold',
        '[&_h3]:mt-4 [&_h3]:text-sm [&_h3]:font-semibold',
        '[&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5',
        '[&_ol]:list-decimal [&_ol]:space-y-1 [&_ol]:pl-5',
        '[&_a]:font-medium [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2',
        '[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.85em]',
        '[&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-3',
        '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
        className,
      )}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function inline(s: string): string {
  return (
    escapeHtml(s)
      // Links: only http(s) and mailto survive, so an escaped `javascript:`
      // payload can't become an href.
      .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+|mailto:[^\s)]+)\)/g, (_m, text, href) => {
        return `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`
      })
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/(^|[\s(])\*([^*\n]+)\*/g, '$1<em>$2</em>')
  )
}

function renderMarkdown(src: string): string {
  const lines = (src ?? '').replace(/\r\n/g, '\n').split('\n')
  const out: string[] = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    if (line.trim().startsWith('```')) {
      const body: string[] = []
      i++
      while (i < lines.length && !lines[i].trim().startsWith('```')) body.push(lines[i++])
      i++ // closing fence
      out.push(`<pre><code>${escapeHtml(body.join('\n'))}</code></pre>`)
      continue
    }

    const heading = line.match(/^(#{1,6})\s+(.*)$/)
    if (heading) {
      const level = Math.min(heading[1].length + 1, 4) // never emit an h1
      out.push(`<h${level}>${inline(heading[2])}</h${level}>`)
      i++
      continue
    }

    if (/^\s*[-*+]\s+/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) {
        items.push(`<li>${inline(lines[i].replace(/^\s*[-*+]\s+/, ''))}</li>`)
        i++
      }
      out.push(`<ul>${items.join('')}</ul>`)
      continue
    }

    if (/^\s*\d+[.)]\s+/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i])) {
        items.push(`<li>${inline(lines[i].replace(/^\s*\d+[.)]\s+/, ''))}</li>`)
        i++
      }
      out.push(`<ol>${items.join('')}</ol>`)
      continue
    }

    if (line.trim() === '') {
      i++
      continue
    }

    // Paragraph: gather until a blank line or the start of another block.
    const para: string[] = []
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^\s*([-*+]|\d+[.)])\s+/.test(lines[i]) &&
      !/^#{1,6}\s/.test(lines[i]) &&
      !lines[i].trim().startsWith('```')
    ) {
      para.push(lines[i++])
    }
    out.push(`<p>${inline(para.join(' '))}</p>`)
  }

  return out.join('')
}
